import {
  Directive,
  ElementRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  Optional,
  Output,
} from '@angular/core';
import { NgControl } from '@angular/forms';
import { GoogleMapsLoaderService } from '../service/google-maps-loader.service';

export interface SelectedPlace {
  address: string;
  lat: number | null;
  lng: number | null;
  city: string;
}

@Directive({
  selector: '[appPlacesAutocomplete]',
  standalone: true,
})
export class PlacesAutocompleteDirective implements OnInit, OnDestroy {
  @Output() placeSelected = new EventEmitter<SelectedPlace>();

  private suggestionClass?: any;
  private tokenClass?: any;
  private token?: any;
  private ready = false;

  private dropdown?: HTMLDivElement;
  private predictions: any[] = [];
  private activeIndex = -1;
  private debounceId?: any;

  constructor(
    private el: ElementRef<HTMLInputElement>,
    private loader: GoogleMapsLoaderService,
    @Optional() private control: NgControl,
  ) {}

  ngOnInit() {
    if (!this.loader.hasKey) return;
    this.el.nativeElement.setAttribute('autocomplete', 'off');
    this.el.nativeElement.addEventListener('input', this.onInput);
    this.el.nativeElement.addEventListener('keydown', this.onKeydown);
    this.el.nativeElement.addEventListener('blur', this.onBlur);
    this.loader
      .load()
      .then(() => this.initApi())
      .catch((err) => console.error('[PlacesAutocomplete]', err));
  }

  ngOnDestroy() {
    this.el.nativeElement.removeEventListener('input', this.onInput);
    this.el.nativeElement.removeEventListener('keydown', this.onKeydown);
    this.el.nativeElement.removeEventListener('blur', this.onBlur);
    window.removeEventListener('scroll', this.reposition, true);
    window.removeEventListener('resize', this.reposition);
    clearTimeout(this.debounceId);
    this.removeDropdown();
  }

  private async initApi() {
    const places: any = await (google.maps as any).importLibrary('places');
    this.suggestionClass = places.AutocompleteSuggestion;
    this.tokenClass = places.AutocompleteSessionToken;
    this.newSession();
    this.ready = true;
  }

  private newSession() {
    if (this.tokenClass) this.token = new this.tokenClass();
  }

  private onInput = () => {
    const value = this.el.nativeElement.value.trim();
    clearTimeout(this.debounceId);
    if (!this.ready || value.length < 3) {
      this.closeDropdown();
      return;
    }
    this.debounceId = setTimeout(() => this.fetch(value), 250);
  };

  private async fetch(input: string) {
    try {
      const { suggestions } = await this.suggestionClass.fetchAutocompleteSuggestions({
        input,
        sessionToken: this.token,
      });
      this.predictions = (suggestions || [])
        .map((s: any) => s.placePrediction)
        .filter((p: any) => !!p);
      this.activeIndex = -1;
      this.renderDropdown();
    } catch (err) {
      console.error('[PlacesAutocomplete]', err);
    }
  }

  private onKeydown = (e: KeyboardEvent) => {
    if (!this.dropdown) {
      if (e.key === 'Enter') e.preventDefault();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      this.move(1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      this.move(-1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (this.activeIndex >= 0) this.choose(this.predictions[this.activeIndex]);
    } else if (e.key === 'Escape') {
      this.closeDropdown();
    }
  };

  private onBlur = () => {
    setTimeout(() => this.closeDropdown(), 150);
  };

  private move(delta: number) {
    if (!this.predictions.length) return;
    this.activeIndex =
      (this.activeIndex + delta + this.predictions.length) % this.predictions.length;
    this.highlight();
  }

  private async choose(prediction: any) {
    try {
      const place = prediction.toPlace();
      await place.fetchFields({
        fields: ['formattedAddress', 'location', 'displayName', 'addressComponents'],
      });
      const address =
        place.formattedAddress || place.displayName || this.el.nativeElement.value;
      const loc = place.location;
      this.setValue(address);
      this.placeSelected.emit({
        address,
        lat: loc ? loc.lat() : null,
        lng: loc ? loc.lng() : null,
        city: this.extractCity(place.addressComponents),
      });
    } catch (err) {
      console.error('[PlacesAutocomplete]', err);
    } finally {
      this.closeDropdown();
      this.newSession();
    }
  }

  private setValue(value: string) {
    if (this.control?.control) this.control.control.setValue(value);
    else this.el.nativeElement.value = value;
  }

  private extractCity(components: any[]): string {
    if (!components?.length) return '';
    const priority = [
      'locality',
      'postal_town',
      'administrative_area_level_3',
      'administrative_area_level_2',
    ];
    for (const type of priority) {
      const match = components.find((c: any) => c?.types?.includes(type));
      if (match) return match.longText || match.shortText || '';
    }
    return '';
  }

  private renderDropdown() {
    if (!this.predictions.length) {
      this.closeDropdown();
      return;
    }
    if (!this.dropdown) {
      this.dropdown = document.createElement('div');
      this.dropdown.className = 'places-dropdown';
      document.body.appendChild(this.dropdown);
      window.addEventListener('scroll', this.reposition, true);
      window.addEventListener('resize', this.reposition);
    }
    this.dropdown.innerHTML = '';
    this.predictions.forEach((p, i) => {
      const main = p.mainText?.text ?? p.text?.text ?? '';
      const secondary = p.secondaryText?.text ?? '';
      const item = document.createElement('div');
      item.className = 'places-item';
      item.innerHTML =
        `<span class="places-item-main">${escapeHtml(main)}</span>` +
        (secondary ? `<span class="places-item-secondary">${escapeHtml(secondary)}</span>` : '');
      item.addEventListener('mousedown', (ev) => {
        ev.preventDefault();
        this.choose(p);
      });
      item.addEventListener('mouseenter', () => {
        this.activeIndex = i;
        this.highlight();
      });
      this.dropdown!.appendChild(item);
    });
    this.reposition();
  }

  private highlight() {
    if (!this.dropdown) return;
    Array.from(this.dropdown.children).forEach((c, i) =>
      c.classList.toggle('active', i === this.activeIndex),
    );
  }

  private reposition = () => {
    if (!this.dropdown) return;
    const r = this.el.nativeElement.getBoundingClientRect();
    this.dropdown.style.top = `${window.scrollY + r.bottom + 4}px`;
    this.dropdown.style.left = `${window.scrollX + r.left}px`;
    this.dropdown.style.width = `${r.width}px`;
  };

  private closeDropdown() {
    this.removeDropdown();
    this.predictions = [];
    this.activeIndex = -1;
  }

  private removeDropdown() {
    if (!this.dropdown) return;
    window.removeEventListener('scroll', this.reposition, true);
    window.removeEventListener('resize', this.reposition);
    this.dropdown.remove();
    this.dropdown = undefined;
  }
}

function escapeHtml(value: string): string {
  const div = document.createElement('div');
  div.textContent = value;
  return div.innerHTML;
}
